import {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  SyntheticEvent,
  useRef,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  CircularProgress,
  Autocomplete,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Grid,
  Checkbox,
} from "@mui/material";
import { debounce } from "lodash";
import useFetchEmployeesList from "src/Pages/Company/OrganizationStructure/api/useFetchEmployeesList";
import __ from "src/utils/Translations";
import {
  Employee,
  IAreaMembers,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import EmployeeChip from "src/Pages/Company/OrganizationStructure/Areas/Components/EmployeeChip";

interface IAutocompleteEmployees {
  companyId: string;
  structureId: string;
}

const AutocompleteEmployees: FC<IAutocompleteEmployees> = ({
  companyId,
  structureId,
}) => {
  const { control, watch } = useFormContext();
  const [filterString, setFilterString] = useState("");
  const [inputValue, setInputValue] = useState("");

  const enabled = !!companyId && !!structureId;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useFetchEmployeesList({ companyId, structureId, enabled, filterString });

  const membersWatch = watch("members") || [];
  const managerWatch = watch("manager");

  const options = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.content) || []).filter(
        (option) => option.id !== managerWatch?.id
      ),
    [data?.pages, managerWatch]
  );

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setFilterString(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(
    (event: SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input") {
        setInputValue(newInputValue);
        debouncedSearch(event, newInputValue);
      } else if (reason === "clear") {
        setInputValue("");
        setFilterString("");
      }
    },
    [debouncedSearch]
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget as HTMLUListElement;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Controller
        name="members"
        control={control}
        render={({ fieldState: { invalid, error }, field }) => (
          <Autocomplete
            multiple
            disableCloseOnSelect
            value={field.value || []}
            inputValue={inputValue}
            filterOptions={(options) => options}
            noOptionsText={
              isFetching ? __("Ładowanie...") : __("Brak wyników wyszukiwania")
            }
            getOptionLabel={(option) =>
              option?.name
                ? `${option.name}${option.fk ? ` (FK: ${option.fk})` : ""}`
                : ""
            }
            isOptionEqualToValue={(option, selectedValue) =>
              option.id === selectedValue?.id
            }
            options={options}
            onChange={(_, newValue) => {
              field.onChange(newValue);
              setInputValue("");
              setFilterString("");
            }}
            onInputChange={handleSearchChange}
            disableListWrap
            disablePortal
            renderInput={(params) => (
              <TextField
                {...params}
                id="members"
                label={__("Dodaj pracownika")}
                error={invalid}
                helperText={error?.message}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetching && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option: Employee, { selected }) => (
              <ListItem
                {...props}
                key={option.id}
                alignItems="center"
                sx={{ display: "flex", alignItems: "center", py: "6px" }}
              >
                <Box display="flex" alignItems="center" mr={2}>
                  <Checkbox checked={selected} />
                  <PresenceIndicator
                    status={option?.presenceInformation?.status}
                  >
                    <Avatar
                      size={6}
                      src={option.avatar || undefined}
                      alt={option.name || ""}
                    >
                      {option.initials ?? ""}
                    </Avatar>
                  </PresenceIndicator>
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      fontSize="14px"
                    >
                      {`${option.name}${
                        option.fk ? ` (FK: ${option.fk})` : ""
                      }`}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="12px"
                    >
                      {typeof option.position === "object" &&
                      option.position !== null
                        ? option.position.label
                        : option.position || null}
                    </Typography>
                  }
                />
              </ListItem>
            )}
            slotProps={{
              listbox: {
                onScroll: handleScroll,
                style: { maxHeight: "300px", overflowY: "auto" },
              },
            }}
            renderTags={() => null}
          />
        )}
      />
      <Box>
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
          }}
        >
          {Array.isArray(membersWatch) &&
            membersWatch.length > 0 &&
            membersWatch.map((member: IAreaMembers) => (
              <Grid size={{ xs: 12, lg: 4 }} key={member.id ?? ""}>
                <EmployeeChip
                  employeeName={member.name ?? ""}
                  fk={member.fk}
                  position={member.position?.label ?? ""}
                  area={member.area?.label ?? ""}
                  initials={member.initials ?? ""}
                  avatar={member.avatar ?? undefined}
                  employeeId={member.id ?? ""}
                  presenceInformation={member.presenceInformation}
                />
              </Grid>
            ))}
        </Grid>
      </Box>
    </>
  );
};

export default AutocompleteEmployees;
